import { useState } from 'react'
import { Loader2, PhoneForwarded, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { normalizeToE164 } from '@/lib/phoneValidation'
import {
  useTransferTargets,
  type TransferAgent,
  type TransferTeam,
} from '@/api/voice'
import { useCallStore } from '@/stores/useCallStore'
import { useCallActions } from '@/hooks/useCallActions'

interface Props {
  darkMode: boolean
  /** `compact` ajuste les paddings/tailles pour tenir dans le widget flottant.
   *  Sinon (modale), on laisse plus d'espace. */
  compact?: boolean
  /** Appelé après un transfert réussi (utile pour fermer une modale parent). */
  onTransferred?: () => void
  /** « Ajouter à l'appel » : un collègue ou un numéro rejoint l'appel sans
   *  annonce, le client reste en ligne (pas d'équipe, pas de mode). */
  ajout?: boolean
}

const CLE_MODE = 'callizy.transfert.mode'

/** « Annoncer d'abord » the first time, then the agent's last choice. */
function modeRetenu(): 'cold' | 'warm' {
  try {
    return localStorage.getItem(CLE_MODE) === 'cold' ? 'cold' : 'warm'
  } catch {
    return 'warm'
  }
}

/**
 * Panneau de transfert d'appel — 3 onglets (Numéro / Collègue / Équipe).
 * Direct (cold) or announced (warm: the agent talks to the colleague first,
 * see AnnonceBar).
 *
 * Partagé entre `InCallWidget` (compact) et `TransferCallModal` (modale agent).
 * Self-contained : gère son state tab/search/destination en interne et appelle
 * directement `useCallActions().transfer`.
 */
export function TransferPanel({ darkMode, compact = false, onTransferred, ajout = false }: Props) {
  const { t } = useTranslation()
  const isTransferring = useCallStore((s) => s.isTransferring)
  const { transfer } = useCallActions()

  const [tab, setTab] = useState<'number' | 'colleague' | 'team'>(ajout ? 'colleague' : 'number')
  const [mode, setModeEtat] = useState<'cold' | 'warm'>(modeRetenu)
  const setMode = (m: 'cold' | 'warm') => {
    setModeEtat(m)
    try {
      localStorage.setItem(CLE_MODE, m)
    } catch {
      /* stockage indisponible : le choix vaut pour cette fois */
    }
  }
  // Ajouter : la personne rejoint l'appel, sans annonce.
  const envoi = ajout ? 'add' : mode
  const [transferTo, setTransferTo] = useState('')
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')

  // On ne fetch agents/teams que quand un onglet non-number est actif.
  const { data: targets, isLoading } = useTransferTargets(tab !== 'number')

  const handleNumberSubmit = async () => {
    setError('')
    if (!transferTo.trim()) {
      setError(t('phone.transfer.errorRequired', 'Numéro requis'))
      return
    }
    const normalized = normalizeToE164(transferTo, 'CA')
    if (!normalized) {
      setError(
        t(
          'phone.transfer.errorFormat',
          'Format invalide. Ex : 4385338446 ou +14385338446',
        ),
      )
      return
    }
    try {
      await transfer('number', normalized, envoi)
      setTransferTo('')
      onTransferred?.()
    } catch {
      // toast déjà émis dans useCallActions
    }
  }

  const handlePickAgent = async (a: TransferAgent) => {
    try {
      await transfer(a.type === 'web' ? 'web_agent' : 'phone_agent', a.id, envoi)
      onTransferred?.()
    } catch {
      /* toast déjà émis */
    }
  }

  const handlePickTeam = async (tm: TransferTeam) => {
    try {
      await transfer('team', tm.id, envoi)
      onTransferred?.()
    } catch {
      /* toast déjà émis */
    }
  }

  const inputH = compact ? 'h-9' : 'h-10'
  const itemPad = compact ? 'py-1.5' : 'py-2'

  return (
    <div className="space-y-2.5">
      {/* Direct or announced */}
      {!ajout && (
        <div
          role="radiogroup"
          aria-label={t('phone.transfer.mode', 'Mode de transfert')}
          className="grid grid-cols-2 gap-1.5"
        >
          {(
            [
              { key: 'cold', label: t('phone.transfer.modeDirect', 'Direct') },
              { key: 'warm', label: t('phone.transfer.modeAnnounce', "Annoncer d'abord") },
            ] as const
          ).map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="radio"
              aria-checked={mode === entry.key}
              onClick={() => setMode(entry.key)}
              disabled={isTransferring}
              className={cn(
                'h-8 rounded-lg border text-xs font-semibold transition-colors disabled:opacity-50',
                mode === entry.key
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : darkMode
                    ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50',
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div
        role="tablist"
        className={cn(
          'grid gap-1 p-0.5 rounded-lg',
          ajout ? 'grid-cols-2' : 'grid-cols-3',
          darkMode ? 'bg-slate-800' : 'bg-slate-100',
        )}
      >
        {(
          [
            { key: 'number', label: t('phone.transfer.tabNumber', 'Numéro') },
            { key: 'colleague', label: t('phone.transfer.tabColleague', 'Collègue') },
            { key: 'team', label: t('phone.transfer.tabTeam', 'Équipe') },
          ] as const
        ).filter((entry) => !ajout || entry.key !== 'team').map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            onClick={() => {
              setTab(entry.key)
              setError('')
              setSearch('')
            }}
            disabled={isTransferring}
            className={cn(
              'h-7 rounded-md text-[11px] font-semibold transition-colors disabled:opacity-50',
              tab === entry.key
                ? darkMode
                  ? 'bg-slate-700 text-white shadow-sm'
                  : 'bg-white text-slate-900 shadow-sm'
                : darkMode
                  ? 'text-slate-400 hover:text-slate-200'
                  : 'text-slate-600 hover:text-slate-900',
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'number' && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <input
              type="tel"
              autoFocus
              value={transferTo}
              onChange={(e) => {
                setTransferTo(e.target.value)
                setError('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleNumberSubmit()
              }}
              disabled={isTransferring}
              placeholder="4385338446"
              className={cn(
                'flex-1 rounded-lg border px-3 text-sm outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50',
                inputH,
                darkMode
                  ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400',
              )}
            />
            <button
              type="button"
              onClick={() => void handleNumberSubmit()}
              disabled={!transferTo.trim() || isTransferring}
              className={cn(
                'px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50',
                inputH,
              )}
            >
              {isTransferring ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : ajout ? (
                <UserPlus className="h-3.5 w-3.5" />
              ) : (
                <PhoneForwarded className="h-3.5 w-3.5" />
              )}
              {ajout
                ? t('phone.ajout.submit', 'Ajouter')
                : isTransferring
                  ? t('phone.transfer.transferring', 'Transfert...')
                  : t('phone.transfer.submit', 'Transférer')}
            </button>
          </div>
          {error && <p className="text-[11px] text-red-500">{error}</p>}
        </div>
      )}

      {tab === 'colleague' && (
        <ListWithSearch
          search={search}
          onSearchChange={setSearch}
          loading={isLoading}
          disabled={isTransferring}
          darkMode={darkMode}
          searchPlaceholder={t('phone.transfer.searchColleague', 'Rechercher un collègue')}
          emptyLabel={t('phone.transfer.emptyColleague', 'Aucun collègue')}
        >
          {(targets?.agents ?? [])
            .filter(
              (a) =>
                !search.trim() ||
                a.name.toLowerCase().includes(search.toLowerCase().trim()),
            )
            .map((a) => (
              <AgentRow
                key={`${a.type}-${a.id}`}
                agent={a}
                darkMode={darkMode}
                disabled={isTransferring}
                itemPad={itemPad}
                libelle={ajout ? t('phone.ajout.submit', 'Ajouter') : undefined}
                onClick={() => void handlePickAgent(a)}
              />
            ))}
        </ListWithSearch>
      )}

      {tab === 'team' && (
        <ListWithSearch
          search={search}
          onSearchChange={setSearch}
          loading={isLoading}
          disabled={isTransferring}
          darkMode={darkMode}
          searchPlaceholder={t('phone.transfer.searchTeam', 'Rechercher une équipe')}
          emptyLabel={t('phone.transfer.emptyTeam', 'Aucune équipe')}
        >
          {(targets?.teams ?? [])
            .filter(
              (tm) =>
                !search.trim() ||
                tm.name.toLowerCase().includes(search.toLowerCase().trim()),
            )
            .map((tm) => (
              <TeamRow
                key={tm.id}
                team={tm}
                darkMode={darkMode}
                disabled={isTransferring || tm.available_count === 0}
                itemPad={itemPad}
                onClick={() => void handlePickTeam(tm)}
              />
            ))}
        </ListWithSearch>
      )}
    </div>
  )
}

// ============================================================
// Sous-composants internes
// ============================================================

interface ListWithSearchProps {
  search: string
  onSearchChange: (v: string) => void
  loading: boolean
  disabled: boolean
  darkMode: boolean
  searchPlaceholder: string
  emptyLabel: string
  children: React.ReactNode
}

function ListWithSearch({
  search,
  onSearchChange,
  loading,
  disabled,
  darkMode,
  searchPlaceholder,
  emptyLabel,
  children,
}: ListWithSearchProps) {
  const items = Array.isArray(children) ? children : [children]
  const visibleCount = items.filter(Boolean).length

  return (
    <div className="space-y-1.5">
      <input
        type="text"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        disabled={disabled}
        className={cn(
          'w-full h-8 rounded-lg border px-2.5 text-xs outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50',
          darkMode
            ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500'
            : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400',
        )}
      />
      <div className="max-h-52 overflow-y-auto -mx-1 px-1 space-y-0.5">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2
              className={cn(
                'h-4 w-4 animate-spin',
                darkMode ? 'text-slate-500' : 'text-slate-400',
              )}
            />
          </div>
        ) : visibleCount === 0 ? (
          <p
            className={cn(
              'text-[11px] text-center py-3',
              darkMode ? 'text-slate-500' : 'text-slate-400',
            )}
          >
            {emptyLabel}
          </p>
        ) : (
          children
        )}
      </div>
    </div>
  )
}

function AgentRow({
  agent,
  darkMode,
  disabled,
  itemPad,
  libelle,
  onClick,
}: {
  agent: TransferAgent
  darkMode: boolean
  disabled: boolean
  itemPad: string
  /** « Ajouter » : a labelled button rather than the transfer icon. */
  libelle?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2 rounded-lg px-2 transition-colors disabled:opacity-50 text-left',
        itemPad,
        darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50',
      )}
    >
      <div className="relative flex-shrink-0">
        <div
          className={cn(
            'h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold',
            darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700',
          )}
        >
          {initials(agent.name)}
        </div>
        {agent.type === 'web' && (
          <span
            className={cn(
              'absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2',
              darkMode ? 'ring-slate-900' : 'ring-white',
              agent.status === 'ONLINE'
                ? 'bg-emerald-500'
                : agent.status === 'BUSY'
                  ? 'bg-amber-500'
                  : 'bg-slate-400',
            )}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-xs font-medium truncate',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {agent.name}
        </p>
        <p
          className={cn(
            'text-[10px] truncate',
            darkMode ? 'text-slate-500' : 'text-slate-500',
          )}
        >
          {agent.type === 'web'
            ? agent.status === 'ONLINE'
              ? 'En ligne'
              : agent.status === 'BUSY'
                ? 'Occupé'
                : 'Hors ligne'
            : agent.phone_e164 || 'Téléphone'}
        </p>
      </div>
      {libelle ? (
        <span className="flex-shrink-0 rounded-md bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white">
          {libelle}
        </span>
      ) : (
        <PhoneForwarded
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0',
            darkMode ? 'text-slate-500' : 'text-slate-400',
          )}
        />
      )}
    </button>
  )
}

function TeamRow({
  team,
  darkMode,
  disabled,
  itemPad,
  onClick,
}: {
  team: TransferTeam
  darkMode: boolean
  disabled: boolean
  itemPad: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2 rounded-lg px-2 transition-colors disabled:opacity-40 text-left',
        itemPad,
        darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50',
      )}
    >
      <span
        className="h-7 w-7 rounded-full flex-shrink-0"
        style={{ backgroundColor: team.color_hex || '#64748b' }}
      />
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-xs font-medium truncate',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {team.name}
        </p>
        <p
          className={cn(
            'text-[10px] truncate',
            darkMode ? 'text-slate-500' : 'text-slate-500',
          )}
        >
          {team.available_count}/{team.member_count} disponible
          {team.available_count > 1 ? 's' : ''}
        </p>
      </div>
      <PhoneForwarded
        className={cn(
          'h-3.5 w-3.5 flex-shrink-0',
          darkMode ? 'text-slate-500' : 'text-slate-400',
        )}
      />
    </button>
  )
}

function initials(name: string): string {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() || '?'
  )
}
